/**
 * Symptom Tracker
 *
 * Daily symptom check-in card for the homepage.
 * Allows users to log symptoms across various categories.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import { View, Pressable, TextInput, ScrollView } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import {
  Utensils,
  HeartHandshake,
  Droplets,
  Droplet,
  Brain,
  Activity,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Plus,
  LucideIcon,
} from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import AnimatedRN, { FadeInDown } from "react-native-reanimated";
import { toLocalDateString, getToday } from "@src/packages/utils/date";
import { useSymptoms, useCreateSymptoms } from "../useCalendarData";
import { formatDate as formatLocalizedDate } from "@src/packages/i18n/formatters";
import { useLocale } from "@src/packages/i18n/useLocale";
import {
  BLEEDING_SYMPTOM_LABELS,
  canonicalBleedingLabel,
} from "../prediction/menstrualBleeding";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// ============================================================================
// Symptom Categories
// ============================================================================

interface SymptomCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  symptoms: string[];
}

const SYMPTOM_CATEGORIES: SymptomCategory[] = [
  {
    id: "digestion",
    label: "Digestion",
    icon: Utensils,
    symptoms: ["Bloating", "Nausea", "Cravings", "Indigestion"],
  },
  {
    id: "sexual",
    label: "Sexual & Reproductive",
    icon: HeartHandshake,
    symptoms: [
      "High Libido",
      "Low Libido",
      "Ovulation Pain",
      "Breast Tenderness",
    ],
  },
  {
    id: "urogenital",
    label: "Urogenital",
    icon: Droplets,
    symptoms: ["Frequent Urination", "Burning", "Incontinence"],
  },
  {
    id: "bleeding",
    label: "Bleeding & Clotting",
    icon: Droplet,
    // Canonical bleeding labels, shared with the Care check-in so one
    // vocabulary reaches the record. `menstrualBleeding` still reads the
    // bare "Light"/"Heavy" these once wrote, for rows already stored.
    symptoms: [
      BLEEDING_SYMPTOM_LABELS.spotting,
      BLEEDING_SYMPTOM_LABELS.light,
      BLEEDING_SYMPTOM_LABELS.heavy,
      "Clots",
    ],
  },
  {
    id: "psychological",
    label: "Psychological",
    icon: Brain,
    symptoms: ["Happy", "Anxious", "Irritable", "Fatigue", "Insomnia"],
  },
  {
    id: "heart",
    label: "Heart & Lungs",
    icon: Activity,
    symptoms: ["Palpitations", "Shortness of Breath"],
  },
];

// ============================================================================
// Props
// ============================================================================

export interface SymptomTrackerProps {
  /** Animation delay in ms */
  delay?: number;
  /** Hide the internal title "Daily Symptom Check-in" (when rendered with external header) */
  hideTitle?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function SymptomTracker({
  delay = 200,
  hideTitle = false,
}: SymptomTrackerProps) {
  const { locale } = useLocale();
  const capturePress = usePressCapture("SymptomTracker");
  const [symptomNote, setSymptomNote] = useState("");
  const [selectedSymptoms, setSelectedSymptoms] = useState<Set<string>>(
    new Set(),
  );
  const [isSymptomsExpanded, setIsSymptomsExpanded] = useState(false);

  // Date navigation state
  const [selectedDate, setSelectedDate] = useState(() => getToday());
  const selectedDateString = useMemo(
    () => toLocalDateString(selectedDate),
    [selectedDate],
  );

  // Check if selected date is today
  const todayString = useMemo(() => toLocalDateString(getToday()), []);
  const isToday = selectedDateString === todayString;

  // Custom symptoms added by user (per category)
  const [customSymptoms, setCustomSymptoms] = useState<
    Record<string, string[]>
  >({});
  const [addingCustomTo, setAddingCustomTo] = useState<string | null>(null);
  const [customSymptomInput, setCustomSymptomInput] = useState("");

  // Fetch symptoms for selected date
  const { data: dateSymptoms = [] } = useSymptoms({
    date: selectedDateString,
  });

  // Navigate to previous day
  const goToPreviousDay = useCallback(() => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  }, []);

  // Navigate to next day
  const goToNextDay = useCallback(() => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  }, []);

  // Format date for display
  const displayDate = useMemo(() => {
    if (isToday) return "Today";
    const yesterday = new Date(getToday());
    yesterday.setDate(yesterday.getDate() - 1);
    if (toLocalDateString(yesterday) === selectedDateString) return "Yesterday";
    return formatLocalizedDate(selectedDate, locale, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }, [selectedDate, selectedDateString, isToday, locale]);

  // Create symptoms mutation
  const createSymptomsMutation = useCreateSymptoms();

  // Track which symptoms have been saved for the selected date
  // Keyed by the current label so a row stored under a retired one still
  // matches its chip, and unticking it deletes the stored row instead of
  // writing a duplicate.
  const savedSymptomNames = useMemo(
    () => new Set(dateSymptoms.map((s) => canonicalBleedingLabel(s.symptom))),
    [dateSymptoms],
  );

  // Initialize selected symptoms from saved symptoms when date changes
  useEffect(() => {
    setSelectedSymptoms(new Set(savedSymptomNames));
  }, [savedSymptomNames]);

  // Add custom symptom to a category
  const handleAddCustomSymptom = useCallback(
    (categoryId: string) => {
      if (!customSymptomInput.trim()) return;
      setCustomSymptoms((prev) => ({
        ...prev,
        [categoryId]: [...(prev[categoryId] || []), customSymptomInput.trim()],
      }));
      setCustomSymptomInput("");
      setAddingCustomTo(null);
    },
    [customSymptomInput],
  );

  // Toggle symptom selection
  const toggleSymptom = useCallback((symptom: string) => {
    setSelectedSymptoms((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(symptom)) {
        newSet.delete(symptom);
      } else {
        newSet.add(symptom);
      }
      return newSet;
    });
  }, []);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    for (const symptom of selectedSymptoms) {
      if (!savedSymptomNames.has(symptom)) return true;
    }
    return false;
  }, [selectedSymptoms, savedSymptomNames]);

  // Handle saving symptoms
  const handleSaveSymptoms = useCallback(() => {
    const symptomsToSave = Array.from(selectedSymptoms).filter(
      (s) => !savedSymptomNames.has(s),
    );

    if (symptomsToSave.length === 0) return;

    createSymptomsMutation.mutate({
      date: selectedDateString,
      symptoms: symptomsToSave,
      notes: symptomNote || null,
    });
  }, [
    selectedSymptoms,
    savedSymptomNames,
    selectedDateString,
    symptomNote,
    createSymptomsMutation,
  ]);

  return (
    <AnimatedRN.View
      entering={FadeInDown.delay(delay)}
      style={{ marginBottom: 24 }}
    >
      <View className="relative rounded-3xl border border-white/10 bg-[#0F172A]/80 shadow-2xl">
        {/* Header */}
        <View className="relative z-10 border-b border-white/5 px-6 pb-4 pt-6">
          <View className="flex-row items-start justify-between">
            <View className="flex-1">
              <Text
                className="mb-1 text-xl font-medium tracking-tight text-white"
                style={{ fontFamily: "Tobias-Thin" }}
              >
                How are you feeling{isToday ? " today" : ""}?
              </Text>
              {!hideTitle && (
                <Text className="text-[0.75rem] text-slate-400">
                  Daily Symptom Check-in
                </Text>
              )}
              <Text
                className={`text-[0.8rem] font-semibold ${isToday ? "text-[#EAB308]" : "text-slate-300"}`}
              >
                {displayDate}
              </Text>
            </View>
            {/* Date Navigation */}
            <View className="flex-row gap-2">
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Previous day"
                onPress={() => {
                  // Chevron-only control; its accessibility label is the copy.
                  capturePress(pressLabel("Previous day"), "previous_day");
                  goToPreviousDay();
                }}
                className="rounded-lg bg-white/5 p-2 active:bg-white/10"
              >
                <ChevronLeft size={18} color="#94A3B8" />
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Next day"
                onPress={() => {
                  // Chevron-only control; its accessibility label is the copy.
                  capturePress(pressLabel("Next day"), "next_day");
                  goToNextDay();
                }}
                className="rounded-lg bg-white/5 p-2 active:bg-white/10"
              >
                <ChevronRight size={18} color="#94A3B8" />
              </Pressable>
            </View>
          </View>
        </View>

        {/* Content */}
        <View className="relative z-10 px-6 pb-5 pt-6">
          {/* Note Input */}
          <TextInput
            value={symptomNote}
            onChangeText={setSymptomNote}
            placeholder="Add a note about today..."
            placeholderTextColor="#475569"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 text-sm font-medium text-slate-200"
          />

          {/* Collapsible Symptom Categories Container */}
          <View
            style={{
              position: "relative",
              maxHeight: isSymptomsExpanded ? undefined : 200,
              overflow: isSymptomsExpanded ? "visible" : "hidden",
            }}
          >
            {/* Symptom Categories */}
            <View style={{ paddingTop: 8 }}>
              {SYMPTOM_CATEGORIES.map((category) => {
                const CategoryIcon = category.icon;
                const categoryCustomSymptoms =
                  customSymptoms[category.id] || [];
                const allSymptoms = [
                  ...category.symptoms,
                  ...categoryCustomSymptoms,
                ];
                const isAddingToThis = addingCustomTo === category.id;

                return (
                  <View key={category.id} style={{ marginTop: 20 }}>
                    <View className="mb-3 flex-row items-center gap-2">
                      <CategoryIcon size={12} color="#94A3B8" />
                      <Text className="text-[0.65rem] font-bold uppercase tracking-widest text-slate-500">
                        {category.label}
                      </Text>
                    </View>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={{ gap: 8 }}
                    >
                      {allSymptoms.map((symptom) => {
                        const isSelected = selectedSymptoms.has(symptom);
                        const isSaved = savedSymptomNames.has(symptom);
                        return (
                          <Pressable
                            key={symptom}
                            onPress={() => {
                              capturePress(pressLabel(symptom), "symptom_chip");
                              toggleSymptom(symptom);
                            }}
                            disabled={createSymptomsMutation.isPending}
                            className={`rounded-full border px-3 py-1.5 ${
                              isSaved
                                ? "border-[#4ADE80]/60 bg-[#4ADE80]/25"
                                : isSelected
                                  ? "border-[#FBBF24]/40 bg-[#FBBF24]/15"
                                  : "border-white/10 bg-white/5"
                            } ${createSymptomsMutation.isPending ? "opacity-50" : ""}`}
                          >
                            <Text
                              className={`text-xs ${
                                isSaved
                                  ? "text-[#4ADE80]"
                                  : isSelected
                                    ? "text-white"
                                    : "text-slate-300"
                              }`}
                            >
                              {isSaved ? `✓ ${symptom}` : symptom}
                            </Text>
                          </Pressable>
                        );
                      })}
                      {/* Add Other Button or Input */}
                      {isAddingToThis ? (
                        <View className="flex-row items-center gap-1">
                          <TextInput
                            value={customSymptomInput}
                            onChangeText={setCustomSymptomInput}
                            placeholder="Type symptom..."
                            placeholderTextColor="#64748B"
                            autoFocus
                            onSubmitEditing={() =>
                              handleAddCustomSymptom(category.id)
                            }
                            onBlur={() => {
                              if (!customSymptomInput.trim()) {
                                setAddingCustomTo(null);
                              }
                            }}
                            className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs text-white"
                            style={{ minWidth: 100 }}
                          />
                          <Pressable
                            onPress={() => {
                              // Plus-icon only, with no visible copy to name it.
                              capturePress("add_custom_symptom");
                              handleAddCustomSymptom(category.id);
                            }}
                            className="rounded-full bg-[#4ADE80]/20 p-1.5"
                          >
                            <Plus size={14} color="#4ADE80" />
                          </Pressable>
                        </View>
                      ) : (
                        <Pressable
                          onPress={() => {
                            capturePress(
                              pressLabel("Other"),
                              "open_custom_symptom",
                            );
                            setAddingCustomTo(category.id);
                          }}
                          className="flex-row items-center gap-1 rounded-full border border-dashed border-white/20 bg-white/5 px-3 py-1.5"
                        >
                          <Plus size={12} color="#64748B" />
                          <Text className="text-xs text-slate-400">Other</Text>
                        </Pressable>
                      )}
                    </ScrollView>
                  </View>
                );
              })}
            </View>

            {/* Gradient Fade Overlay - only when collapsed */}
            {!isSymptomsExpanded && (
              <LinearGradient
                colors={[
                  "transparent",
                  "rgba(15, 23, 42, 0.95)",
                  "rgba(15, 23, 42, 1)",
                ]}
                locations={[0, 0.6, 1]}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 80,
                  pointerEvents: "none",
                }}
              />
            )}
          </View>

          {/* Expand/Collapse and Save Buttons */}
          <View className="mt-4 flex-row items-center justify-center gap-3">
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(isSymptomsExpanded ? "View fewer" : "View all"),
                  isSymptomsExpanded ? "collapse_symptoms" : "expand_symptoms",
                );
                setIsSymptomsExpanded(!isSymptomsExpanded);
              }}
              className="flex-row items-center gap-2 rounded-full border border-white/10 bg-white/[0.08] px-5 py-2 shadow-lg active:scale-95"
            >
              <Text className="text-xs font-semibold text-slate-200">
                {isSymptomsExpanded ? "View fewer" : "View all"}
              </Text>
              {isSymptomsExpanded ? (
                <ChevronUp size={14} color="#94A3B8" />
              ) : (
                <ChevronDown size={14} color="#94A3B8" />
              )}
            </Pressable>

            {hasUnsavedChanges && (
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(
                      createSymptomsMutation.isPending ? "Saving..." : "Save",
                    ),
                    "save_symptoms",
                  );
                  handleSaveSymptoms();
                }}
                disabled={createSymptomsMutation.isPending}
                className={`rounded-full border border-[#4ADE80]/30 bg-[#4ADE80]/20 px-5 py-2 shadow-lg active:scale-95 ${
                  createSymptomsMutation.isPending ? "opacity-50" : ""
                }`}
              >
                <Text className="text-xs font-semibold text-[#4ADE80]">
                  {createSymptomsMutation.isPending ? "Saving..." : "Save"}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    </AnimatedRN.View>
  );
}
